import axios from "axios";
import {
    MDBBtn,
    MDBCard,
    MDBCardHeader,
    MDBCheckbox,
    MDBCol,
    MDBContainer,
    MDBListGroup,
    MDBListGroupItem,
    MDBRow,
} from "mdb-react-ui-kit";
import React, { useContext, useEffect, useState } from "react";
import { DomainContext } from "../context/DomainProvider";

const ClassroomSetup = () => {
    const [roles, setRoles] = useState([]);
    const [syllabus, setSyllabus] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedDemos, setSelectedDemos] = useState([]);
    const [error, setError] = useState("");
    const baseUrl = process.env.REACT_APP_INVENTORY_URL;
    const { domainId } = useContext(DomainContext);

    // Fetch roles and syllabus
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await axios.get(`${baseUrl}/users/roles/all`);
                setRoles(response.data || []);
            } catch (err) {
                setError("Failed to fetch roles. Please try again later.");
                console.error("Error fetching roles:", err);
            }
        };

        const fetchSyllabus = async () => {
            try {
                const response = await axios.get(`${baseUrl}/demo/domain/${domainId}`);
                setSyllabus(response.data || []);
            } catch (err) {
                setError("Failed to fetch syllabus. Please try again later.");
                console.error("Error fetching syllabus:", err);
            }
        };

        fetchRoles();
        fetchSyllabus();
    }, [baseUrl, domainId]);

    // Handle role selection
    const handleRoleSelection = (role) => {
        setSelectedRoles((prev) =>
            prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
        );
    };

    // Handle group selection
    const handleGroupSelection = (groupDn) => {
        setSelectedGroups((prev) =>
            prev.includes(groupDn) ? prev.filter((g) => g !== groupDn) : [...prev, groupDn]
        );
    };

    // Handle user selection
    const handleUserSelection = (userDn) => {
        setSelectedUsers((prev) =>
            prev.includes(userDn) ? prev.filter((u) => u !== userDn) : [...prev, userDn]
        );
    };

    // Handle demo selection
    const handleDemoSelection = (demo) => {
        setSelectedDemos((prev) =>
            prev.includes(demo) ? prev.filter((d) => d !== demo) : [...prev, demo]
        );
    };

    // Extract groups for selected roles
    const renderGroupsForRoles = () => {
        const filteredGroups = roles
            .filter((role) => selectedRoles.includes(role.name)) // Match selected roles
            .flatMap((role) => role.groups || []); // Extract groups from selected roles

        console.log("Filtered groups for selected roles:", filteredGroups);

        return filteredGroups.map((group) => (
            <MDBListGroupItem tag="label" key={group.groupDn}>
                <MDBCheckbox
                    label={group.groupName}
                    onChange={() => handleGroupSelection(group.groupDn)}
                    checked={selectedGroups.includes(group.groupDn)}
                />
            </MDBListGroupItem>
        ));
    };

    // Extract users for selected groups
    const renderUsersForGroups = () => {
        const filteredUsers = roles
            .flatMap((role) => role.groups || []) // Get all groups from roles
            .filter((group) => selectedGroups.includes(group.groupDn)) // Filter by selected groups
            .flatMap((group) => group.users || []); // Extract users from groups

        console.log("Filtered users for selected groups:", filteredUsers);

        return filteredUsers.map((userDn) => (
            <MDBListGroupItem tag="label" key={userDn}>
                <MDBCheckbox
                    label={userDn.match(/uid=([^,]+)/)?.[1] || userDn} // Extract UID from DN
                    onChange={() => handleUserSelection(userDn)}
                    checked={selectedUsers.includes(userDn)}
                />
            </MDBListGroupItem>
        ));
    };

    return (
        <MDBContainer breakpoint="lg"  className='align-items-center justify-content-center mt-5'>
            {error && <p className="text-danger">{error}</p>}

            <MDBRow  className='align-items-center justify-content-center'>
                {/* Roles Column */}
                <MDBCol size="3"  className='align-items-center justify-content-center'>
                  <MDBCard>
                    <MDBCardHeader>  <h5 className="text-center">Roles</h5></MDBCardHeader>
                  
                    <MDBListGroup>
                        {roles.map((role) => (
                            <MDBListGroupItem tag="label" key={role.dn}>
                                <MDBCheckbox
                                    label={role.name}
                                    onChange={() => handleRoleSelection(role.name)}
                                    checked={selectedRoles.includes(role.name)}
                                />
                            </MDBListGroupItem>
                        ))}
                    </MDBListGroup>
                    </MDBCard>
                </MDBCol>

                {/* Groups Column */}
                <MDBCol size="3"  className='align-items-center justify-content-center'>
                <MDBCard>
                <MDBCardHeader>
                    <h5 className="text-center">Groups</h5></MDBCardHeader>
                    <MDBListGroup>{renderGroupsForRoles()}</MDBListGroup>
                    </MDBCard>
                </MDBCol>

                {/* Users Column */}
                <MDBCol size="3"  className='align-items-center justify-content-center'>
                <MDBCard>
                <MDBCardHeader>
                    <h5 className="text-center">Users</h5></MDBCardHeader>
                    <MDBListGroup>{renderUsersForGroups()}</MDBListGroup>
                    </MDBCard>
                </MDBCol>

                {/* Syllabus Column */}
                <MDBCol size="3"  className='align-items-center justify-content-center'>
                <MDBCard>
                <MDBCardHeader>
                    <h5 className="text-center">Syllabus</h5></MDBCardHeader>
                    <MDBListGroup>
                        {syllabus.map((demo) => (
                            <MDBListGroupItem tag="label" key={demo.id}>
                                <MDBCheckbox
                                    label={demo.companyName || demo.name} // Use companyName or name
                                    onChange={() => handleDemoSelection(demo.id)}
                                    checked={selectedDemos.includes(demo.id)}
                                />
                            </MDBListGroupItem>
                        ))}
                    </MDBListGroup>
                    </MDBCard>
                </MDBCol>
            </MDBRow>

            {/* Submit Button */}
            <div className="mt-4 text-center">
                <MDBBtn
                    onClick={() => {
                        console.log("Selected Roles:", selectedRoles);
                        console.log("Selected Groups:", selectedGroups);
                        console.log("Selected Users:", selectedUsers);
                        console.log("Selected Syllabus:", selectedDemos);
                    }}
                >
                    Create Class
                </MDBBtn>
            </div>
        </MDBContainer>
    );
};

export default ClassroomSetup;

  

